/**
 * Client configuration + canonical AOI cell math.
 *
 * Re-exports the shared constants and provides the client-side cell helpers
 * (identical math to `server/src/lib/spatial.ts`) so the subscription window the
 * client computes matches the cells the server stamps on every write.
 */

export {
  TICK_HZ,
  FIXED_DT,
  CELL_SIZE_M,
  AOI_RADIUS_CELLS,
  AOI_HYSTERESIS_CELLS,
  MOVE_SPEED_MPS,
  SPRINT_SPEED_MPS,
  DEFAULT_PERSONAL_WORLD_SIZE_M,
  WORLD_KIND,
  CURRENCY,
  MARKET_TAX,
} from '@shared/constants.js';

import { CELL_SIZE_M, AOI_RADIUS_CELLS } from '@shared/constants.js';

/**
 * True when the page itself is served over HTTPS (dev-https for WebXR/Quest,
 * or production behind the site's real certificate). A secure page cannot
 * open ws:// or fetch http:// (mixed content), and on a headset `localhost`
 * is the headset — so secure contexts route SpacetimeDB + MinIO through the
 * SAME-ORIGIN vite proxies (/stdb, /assets-proxy) declared in vite.config.
 */
const SECURE_PAGE = typeof location !== 'undefined' && location.protocol === 'https:';

/**
 * SpacetimeDB WebSocket endpoint (self-hosted). Secure pages use the BARE
 * origin (not a path prefix): the SDK builds its socket URL with
 * `new URL('v1/database/…', uri)` — RELATIVE resolution that silently DROPS
 * a path prefix like `/stdb` (the Quest's silent connect-hang, 2026-08-03) —
 * so the vite proxy forwards the SDK's real `/v1` namespace instead.
 */
export const STDB_WS_URI: string = SECURE_PAGE
  ? `wss://${location.host}`
  : 'ws://localhost:3000';
/** Published database name. */
export const STDB_DB_NAME = 'atlas';
/** localStorage key for the persisted auth token (resumes the same identity). */
export const TOKEN_KEY = 'atlas_stdb_token';

/** Generated alien-ground texture for the Commons terrain (MinIO key under atlas-assets). */
export const TERRAIN_TEXTURE_KEY = 'textures/regolith_ground.png';
/** The three ground albedos blended across the terrain for variety (regolith base + moss + dirt patches). */
export const TERRAIN_GROUND_TEXTURES = {
  regolith: 'textures/regolith_ground.png',
  moss: 'textures/glow_moss_ground.png',
  dirt: 'textures/packed_dirt.png',
} as const;
/** UV tiling: each ground texture repeats this many times across the 512 m terrain (higher = crisper). */
export const TERRAIN_TEXTURE_SCALE = 22;

/** Side length (meters) of one terrain chunk in the Commons streamer. */
export const CHUNK_SIZE = 256;
/** Radius (in chunks) of the visible window around the player. Window = (2r+1)² = 49 chunks. */
export const CHUNK_RADIUS = 3;
/** Vertex segments along each axis for near chunks (Chebyshev distance ≤ 1). */
export const CHUNK_SEG_NEAR = 48;
/** Vertex segments along each axis for far chunks (Chebyshev distance > 1). */
export const CHUNK_SEG_FAR = 24;
/** Coarse segments for HORIZON chunks (the extended long-draw-distance rings,
 *  Chebyshev > CHUNK_HORIZON_RING) — a bare heightfield skirt, cheap tris. */
export const CHUNK_SEG_HORIZON = 10;
/** Beyond this ring a chunk uses the horizon (coarse) segment count. */
export const CHUNK_HORIZON_RING = 6;
/**
 * Terrain-worker stall watchdog (ms): with jobs in flight, this long without a
 * single worker MESSAGE (job ack or result) declares the worker dead and falls
 * back to sync builds. A wedged module load (cold dev-server dep-optimize)
 * never fires an 'error' event, so a wall-clock deadline is the only detection.
 * 20 s not 10: a stamped chunk that regenerates an un-baked CITY plan computes
 * silently for 5–13 s — a legitimate gap that must not trip the watchdog
 * (a false trip re-introduces the main-thread plan-generation hitch).
 */
export const CHUNK_WORKER_STALL_MS = 20_000;
// First-boot budget: a module worker's import graph over cold vite + LAN TLS
// (the Quest 3 path) is dozens of sequential fetches — 20 s false-killed a
// HEALTHY worker and doomed the whole session to main-thread chunk builds
// (~35 ms of jank per chunk on a mobile CPU — the "VR lag" field report).
export const CHUNK_WORKER_BOOT_MS = 60_000;

/**
 * Per-biome ground albedo asset paths. Keys match the `Biome` discriminator from
 * `@shared/terrain.ts`. Task 4's terrain streamer resolves these against
 * `ASSET_STORE_BASE` at load time; they need not exist on disk for typecheck/build.
 */
export const BIOME_GROUND: Record<string, string> = {
  regolith_plains: 'textures/regolith_ground.png',
  crystal_forest:  'textures/crystal_ground.png',
  dune_desert:     'textures/sand.png',
  rocky_highlands: 'textures/packed_dirt.png',
  moss_wetland:    'textures/glow_moss_ground.png',
  fungal_basin:    'textures/spore_soil.png',
};

/**
 * Base URL of the self-hosted MinIO asset store (content-addressed render assets,
 * G0). The `render_asset` registry stores a MinIO object KEY; the client resolves
 * it to `${ASSET_STORE_BASE}/${key}` (never hardcoding a URL). Override with
 * `VITE_ASSET_STORE_BASE` — this dev box runs MinIO on :9100 (another project holds
 * :9000); a clean docker-compose machine uses :9000.
 */
export const ASSET_STORE_BASE: string = SECURE_PAGE
  // Secure page: same-origin proxy (an http:// env override would be blocked
  // as mixed content anyway; vite.config's ATLAS_MINIO_ORIGIN retargets the
  // proxy itself when the store isn't on :9100).
  ? `https://${location.host}/assets-proxy/atlas-assets`
  : ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_ASSET_STORE_BASE ??
    'http://localhost:9000/atlas-assets');

/** Map a world-space coordinate (meters) to its integer AOI cell index. */
export function worldToCell(coord: number): number {
  return Math.floor(coord / CELL_SIZE_M);
}

/** Manhattan distance (in cells) between two cells. */
export function cellManhattan(ax: number, az: number, bx: number, bz: number): number {
  return Math.abs(ax - bx) + Math.abs(az - bz);
}

export interface CellRange {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Inclusive AOI cell window around a center cell. */
export function cellRange(cx: number, cz: number, radius = AOI_RADIUS_CELLS): CellRange {
  return { minX: cx - radius, maxX: cx + radius, minZ: cz - radius, maxZ: cz + radius };
}
